<template>
	<article :class="['coupon-card', themeClass]" tabindex="0">
		<div class="coupon-card__layout">
			<!-- 券类型、类型图标与金额装饰图 -->
			<div class="coupon-card__visual">
				<span class="coupon-card__type">
					<i class="iconfont icon-biaoqian" aria-hidden="true"></i>
					{{ coupon.couponTypeName }}
				</span>
				<span class="coupon-card__amount-tag" aria-hidden="true"></span>
			</div>

			<div class="coupon-card__content">
				<div class="coupon-card__body">
					<!-- 券名称、适用范围、使用门槛、最高优惠 -->
					<div class="coupon-card__details">
						<!-- 券名称 -->
						<h3>{{ coupon.couponName }}</h3>
						<!-- 适用范围 -->
						<p>{{ coupon.applicableScopeName }}</p>
						<!-- 使用门槛 -->
						<p>{{ thresholdText }}</p>
						<!-- 最高优惠 -->
						<p>{{ discountCapText }}</p>
					</div>

					<!-- 面值与叠加策略 -->
					<div class="coupon-card__value">
						<!-- 面值 -->
						<div>
							<span v-if="valueUnit === '元'" class="currency">¥</span>
							<strong>{{ coupon.faceValue }}</strong>
							<span v-if="valueUnit !== '元'">{{ valueUnit }}</span>
						</div>
						<!-- 叠加策略 -->
						<p :title="stackStrategyText">{{ stackStrategyText }}</p>
					</div>
				</div>

				<!-- 有效期、领取进度与领取按钮 -->
				<footer class="coupon-card__footer">
					<div class="coupon-card__meta">
						<!-- 有效期 -->
						<p>{{ validityText }}</p>
						<!-- 已领数量、发放量与领取率 -->
						<div class="coupon-card__stock">
							<span>已领{{ coupon.receivedQuantity }}/{{ coupon.totalQuantity }}</span>
							<i><b :style="{ width: claimRate + '%' }"></b></i>
							<span>{{ claimRate }}%</span>
						</div>
					</div>
					<!-- 领取按钮 -->
					<button type="button" :disabled="isDisabled" @click.stop="$emit('claim', coupon)">
						{{ buttonText }}
					</button>
				</footer>
			</div>
		</div>
	</article>
</template>

<script>
	export default {
		name: 'CouponCard',
		props: {
			coupon: {
				type: Object,
				required: true
			},
			claiming: {
				type: Boolean,
				default: false
			}
		},
		computed: {
			// 已领取数量达到发放总量时，统一切换为“已领完”主题和禁用状态
			isSoldOut() {
				const totalQuantity = Number(this.coupon.totalQuantity) || 0
				const receivedQuantity = Number(this.coupon.receivedQuantity) || 0
				return this.coupon.claimStatus === 'SOLD_OUT' || (totalQuantity > 0 && receivedQuantity >= totalQuantity)
			},
			isClaimed() {
				return this.coupon.claimStatus === 'CLAIMED'
			},
			isDisabled() {
				return this.claiming || this.isClaimed || this.isSoldOut || this.coupon.canClaim === false
			},
			// 领取率由 receivedQuantity / totalQuantity 实时计算，异常值按 0 处理并限制最大为 100%
			claimRate() {
				const totalQuantity = Number(this.coupon.totalQuantity) || 0
				const receivedQuantity = Number(this.coupon.receivedQuantity) || 0
				if (totalQuantity <= 0) return 0
				return Math.min(100, Number(((receivedQuantity / totalQuantity) * 100).toFixed(2)))
			},
			// couponType 字典：10 满减券、20 折扣券、30 体验券；领完状态优先使用灰色主题
			themeClass() {
				if (this.isSoldOut) return 'coupon-card--sold-out'
				const themeClasses = {
					10: 'coupon-card--full-reduction',
					20: 'coupon-card--discount',
					30: 'coupon-card--trial'
				}
				return themeClasses[Number(this.coupon.couponType)] || ''
			},
			// 体验券的面值单位由 measureType 决定：10 次数型、20 时间型
			valueUnit() {
				const couponType = Number(this.coupon.couponType)
				if (couponType === 10) return '元'
				if (couponType === 20) return '折'
				if (couponType === 30) return Number(this.coupon.measureType) === 20 ? '天' : '次'
				return ''
			},
			// thresholdAmount 为 0 时表示无门槛
			thresholdText() {
				const thresholdAmount = Number(this.coupon.thresholdAmount) || 0
				return thresholdAmount > 0 ? `满${thresholdAmount}元可用` : '无门槛'
			},
			// discountCapAmount 为 0 时表示不限制最高优惠
			discountCapText() {
				const discountCapAmount = Number(this.coupon.discountCapAmount) || 0
				return discountCapAmount > 0 ? `${discountCapAmount}元` : '无最高优惠限制'
			},
			// stackStrategy 字典：10 不可叠加、20 可叠加、30 互斥
			stackStrategyText() {
				const strategyNames = { 10: '不可叠加', 20: '可叠加', 30: '互斥使用' }
				return strategyNames[Number(this.coupon.stackStrategy)] || '叠加规则以券面为准'
			},
			// 当前字段只提供使用截止时间，因此券面统一展示 useEndTime
			validityText() {
				if (!this.coupon.useEndTime) return '长期有效'
				return `有效期至${String(this.coupon.useEndTime).slice(0, 10)}`
			},
			buttonText() {
				if (this.claiming) return '领取中...'
				if (this.isClaimed) return '已领取'
				return this.isSoldOut ? '已领完' : '立即领取'
			}
		}
	}
</script>

<style lang="scss" scoped>
	.coupon-card {
		position: relative;
		box-sizing: border-box;
		min-height: 179px;
		padding: 14px 17px 12px;
		outline: none;
		color: #666666;
		background-position: center;
		background-size: 100% 100%;
		background-repeat: no-repeat;
		transition: transform 0.25s ease, filter 0.25s ease;

		&:hover,
		&:focus-visible {
			z-index: 2;
			transform: translateY(-5px);
			filter: drop-shadow(0 10px 12px rgba(250, 108, 33, 0.16));
		}
	}

	.coupon-card--full-reduction {
		/* 满减券主题 */
		--theme: #df4518;
		--field: #f96a21;
		--tag: #fd663d;
		--divider: #fdb955;
		--button-start: #ffc578;
		--button-end: #fa6c21;
		--button-shadow: rgba(250, 108, 33, 0.38);
		background-image: url('~@/assets/coupon/full-reduction-coupon-bg.png');

		&:hover,
		&:focus-visible {
			filter: drop-shadow(0 10px 12px rgba(250, 108, 33, 0.16));
		}
	}

	.coupon-card--trial {
		/* 体验券主题 */
		--theme: #3072f9;
		--field: #4a8bf9;
		--tag: #4297fa;
		--divider: #d0e6fd;
		--button-start: #6fb5ff;
		--button-end: #0c6ef5;
		--button-shadow: rgba(12, 110, 245, 0.34);
		background-image: url('~@/assets/coupon/trial-coupon-bg.png');

		&:hover,
		&:focus-visible {
			filter: drop-shadow(0 10px 12px rgba(12, 110, 245, 0.16));
		}
	}

	.coupon-card--discount {
		/* 折扣券主题 */
		--theme: #7045f1;
		--field: #997af6;
		--tag: #8b5ef5;
		--divider: #d7c5fb;
		--button-start: #ceb8ff;
		--button-end: #7143ef;
		--button-shadow: rgba(113, 67, 239, 0.35);
		background-image: url('~@/assets/coupon/discount-coupon-bg.png');

		&:hover,
		&:focus-visible {
			filter: drop-shadow(0 10px 12px rgba(113, 67, 239, 0.16));
		}
	}

	.coupon-card--sold-out {
		/* 已领完主题 */
		--theme: #666666;
		--field: #666666;
		--tag: #666666;
		--divider: #c9c9c9;
		--button-start: #d4d4d4;
		--button-end: #666666;
		--button-shadow: rgba(102, 102, 102, 0.28);
		background-image: url('~@/assets/coupon/coupon-disabled-bg.png');
		filter: grayscale(1);

		&:hover,
		&:focus-visible {
			filter: grayscale(1) drop-shadow(0 9px 11px rgba(102, 102, 102, 0.16));
		}
	}
	.coupon-card__layout {
		display: grid;
		grid-template-columns: 91px minmax(0, 1fr);
		column-gap: 14px;
	}

	.coupon-card__visual {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}
	.coupon-card__type {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 67px;
		height: 23px;
		box-sizing: border-box;
		padding: 0 8px;
		border-radius: 12px;
		color: #ffffff;
		background: var(--tag);
		font-size: 12px;
		white-space: nowrap;
	}
	.coupon-card__type .iconfont {
		margin-right: 4px;
		font-size: 12px;
	}
	.coupon-card__amount-tag {
		display: block;
		width: 91px;
		height: 75px;
		margin: 25px 0 0 -4px;
		background-position: center;
		background-size: contain;
		background-repeat: no-repeat;
		transition: transform 0.25s ease;
	}
	.coupon-card--full-reduction .coupon-card__amount-tag {
		background-image: url('~@/assets/coupon/full-reduction-coupon-amount-tag.png');
	}
	.coupon-card--trial .coupon-card__amount-tag {
		background-image: url('~@/assets/coupon/trial-coupon-amount-tag.png');
	}
	.coupon-card--discount .coupon-card__amount-tag {
		background-image: url('~@/assets/coupon/discount-coupon-amount-tag.png');
	}
	.coupon-card--sold-out .coupon-card__amount-tag {
		background-image: url('~@/assets/coupon/coupon-disabled-amount-tag.png');
	}
	.coupon-card:hover .coupon-card__amount-tag {
		transform: translateY(-3px) rotate(-3deg) scale(1.04);
	}
	.coupon-card__content {
		display: flex;
		min-width: 0;
		flex-direction: column;
		justify-content: space-between;
	}
	.coupon-card__body {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 108px;
		column-gap: 10px;
		min-height: 105px;
	}

	.coupon-card__details {
		box-sizing: border-box;
		border-right: 1px dashed var(--divider);
	}
	.coupon-card__details h3 {
		min-width: 0;
		margin: 0 0 7px;
		overflow: hidden;
		color: var(--theme);
		font-size: 17px;
		line-height: 22px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.coupon-card__details p {
		margin: 2px 0 0;
		overflow: hidden;
		color: var(--field);
		font-size: 12px;
		line-height: 18px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.coupon-card__value {
		padding-top: 18px;
		text-align: center;
	}
	.coupon-card__value > div {
		height: 41px;
		color: var(--theme);
		line-height: 41px;
		white-space: nowrap;
	}
	.coupon-card__value strong {
		font-size: 36px;
	}
	.coupon-card__value span {
		font-size: 17px;
		font-weight: 700;
	}
	.coupon-card__value .currency {
		font-size: 18px;
	}
	.coupon-card__value p {
		display: -webkit-box;
		margin: 3px 0 0;
		overflow: hidden;
		color: #666666;
		font-size: 12px;
		line-height: 18px;
		word-break: break-all;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
	}

	.coupon-card__footer {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		column-gap: 10px;
	}
	.coupon-card__meta {
		width: 202px;
		color: #666666;
		font-size: 12px;
		padding-right: 15px;
	}
	.coupon-card__meta p {
		margin: 0 0 5px;
		white-space: nowrap;
	}
	.coupon-card__stock {
		display: flex;
		align-items: center;
		gap: 5px;
	}
	.coupon-card__stock > i {
		flex: 1;
		height: 6px;
		overflow: hidden;
		border-radius: 3px;
		background: rgba(102, 102, 102, 0.12);
	}
	.coupon-card__stock b {
		display: block;
		height: 100%;
		border-radius: inherit;
		background: var(--theme);
		transition: width 0.35s ease;
	}
	.coupon-card__footer button {
		width: 108px;
		height: 34px;
		border: 0;
		border-radius: 17px;
		outline: none;
		color: #ffffff;
		background: linear-gradient(180deg, var(--button-start), var(--button-end));
		box-shadow: 0 5px 10px var(--button-shadow);
		font-size: 14px;
		cursor: pointer;
		transition: transform 0.2s ease, box-shadow 0.2s ease;
	}
	.coupon-card__footer button:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 7px 14px var(--button-shadow);
	}
	.coupon-card__footer button:active:not(:disabled) {
		transform: translateY(0);
	}
	.coupon-card__footer button:disabled {
		cursor: not-allowed;
		opacity: 0.72;
	}

	@media (prefers-reduced-motion: reduce) {
		.coupon-card,
		.coupon-card__amount-tag,
		.coupon-card__footer button {
			transition: none;
		}
	}
</style>
